import { Injectable, OnDestroy, inject } from "@angular/core";
import { firstValueFrom, Subject, Subscription } from "rxjs";

import { IMappingViewModel } from "../../settings-interfaces/mapping-interfaces/mapping-view-model.interface";
import {
    CardViewService,
    HelpersService,
    ResponseErrorHandlerService,
    StepsService,
    DashboardsService,
    TaxReturnStatusesService,
    AnalyticsService,
    IFirmRolesService,
    ViewType,
} from "../../shared";

import { MappingOfStatusesService } from "../../shared/mapping-of-statuses-service/mapping-of-statuses.service";
import { ITaxReturnStatus } from "../../views-interfaces/tax-return-status-model.interface";
import { IStepsModel } from "src/app/views-interfaces/steps-model.interface";
import { isJobsBilling } from "src/app/views-interfaces";

@Injectable({ providedIn: "root" })
export class SettingMappingModelService implements OnDestroy {
    private stepsService = inject(StepsService);
    private responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private taxReturnStatusesService = inject(TaxReturnStatusesService);
    private mappingOfStatusesService = inject(MappingOfStatusesService);
    private cardViewService = inject(CardViewService);
    private helpersService = inject(HelpersService);
    private readonly analyticsService = inject(AnalyticsService);
    private dashboardService = inject(DashboardsService);
    private iFirmRolesService = inject(IFirmRolesService);

    private SettingMappingModelDone = new Subject<boolean>();

    subscription: Subscription;
    isEditingSubscription: Subscription;
    mappingViewmodel: any = {};
    originalStatuses: any = [];
    isSaving = 0;
    inError = false;
    Steps: any;
    isEditInProgress = false;

    settingMappingModelDone$ = this.SettingMappingModelDone.asObservable();

    constructor() {
        const helpersService = this.helpersService;

        this.subscription = this.dashboardService.currentDashboardChanged$.subscribe(async () => {
            await this.init();
        });
        this.isEditingSubscription = helpersService.placeLabelIsEditProgressEvent$.subscribe((isEditing) => {
            this.isEditInProgress = isEditing;
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        this.isEditingSubscription.unsubscribe();
    }

    public async init() {
        if (!this.helpersService.stepsMerged) {
            await this.helpersService.mergeSteps();
        }
        this.mappingViewmodel = await this.sortData();
        this.mappingViewmodel.showScroll = false;
        this.getOriginalStatuses();
        this.mappingViewmodel.unassigned = await this.prepareTaxReturnStatuses();
        this.SettingMappingModelDone.next(true);
    }

    private async sortData(): Promise<IMappingViewModel> {
        const sortedData: any = {
            active: [],
            notStarted: [],
            completed: [],
        };
        this.Steps = structuredClone(this.mappingOfStatusesService.Steps);
        for (const step of this.Steps) {
            for (const status of step.Statuses) {
                if (!status.RoleId) {
                    status.RoleId = "unassigned";
                }
                status.TaxReturnStatuse = structuredClone(await this.taxReturnStatusesService.getTaxReturnStatusById(status.TaxReturnStatusId));
            }
            sortedData[step.State].push(step);
        }

        sortedData.active.sort((a, b) => a.Order - b.Order);
        sortedData.notStarted.sort((a, b) => a.Order - b.Order);
        sortedData.completed.sort((a, b) => a.Order - b.Order);
        return sortedData;
    }

    private async getOriginalStatuses() {
        this.originalStatuses = (await this.taxReturnStatusesService.getTaxReturnStatusesList()).map((item: any) => {
            const status: any = {};
            status.TaxReturnStatuse = {
                Name: {
                    "en-CA": "",
                    "fr-CA": "",
                },
            };
            status.TaxReturnStatuse.Name["en-CA"] = item.Name["en-CA"];
            status.TaxReturnStatuse.Name["fr-CA"] = item.Name["fr-CA"];
            status.TaxReturnStatusId = item.Id;
            status.TaxReturnStatuse.Active = item.Active;
            return status;
        });
    }

    async prepareTaxReturnStatuses() {
        let preparedStatuses: any = [];
        const TaxReturnStatuses = structuredClone(await this.taxReturnStatusesService.getTaxReturnStatusesList());
        preparedStatuses = TaxReturnStatuses.map((item: any) => {
            item.TaxReturnStatuse = {};
            item.TaxReturnStatuse.Name = item.Name;
            item.TaxReturnStatuse.TaxAppStatusId = item.TaxAppStatusId;
            item.TaxReturnStatuse.Active = item.Active;
            item.TaxReturnStatusId = item.Id;
            item.RoleId = "unassigned";
            return item;
        });

        function compare(a, b) {
            if (+a.TaxAppStatusId < +b.TaxAppStatusId) {
                return -1;
            }
            if (+a.TaxAppStatusId > +b.TaxAppStatusId) {
                return 1;
            }
            return 0;
        }

        preparedStatuses.sort(compare);
        return this.removeAssignedStatuses(preparedStatuses);
    }

    private removeAssignedStatuses(preparedStatuses: any): any {
        this.Steps.forEach((step) => {
            step.Statuses.forEach((status) => {
                for (let i = 0; i < preparedStatuses.length; i++) {
                    if (preparedStatuses[i].Id === status.TaxReturnStatusId) {
                        preparedStatuses.splice(i, 1);
                    }
                }
            });
        });
        return preparedStatuses;
    }

    async savingData(data: IMappingViewModel) {
        this.isSaving = 0;
        this.inError = false;
        const currentDashboard = await this.dashboardService.getCurrentDashboard(true);
        const isJobsBillingDashboard = isJobsBilling(currentDashboard?.ApplicationType);

        const preparedData = {
            addSteps: [],
            updateSteps: {} as IStepsModel,
            allSteps: [],
        };

        this.cardViewService.settingsSectionChanged = true;

        await Promise.all(
            this.mappingViewmodel.unassigned.map(async (taxReturnStatuse) => {
                if (taxReturnStatuse.TaxReturnStatuse?.Id) {
                    await this.savingData_findChangedStatuses(taxReturnStatuse.TaxReturnStatuse);
                } else {
                    await this.savingData_findChangedStatuses(taxReturnStatuse);
                }
            }),
        );

        if (data.active.length > 0) {
            await Promise.all(
                data.active.map(async (item: any, i: number) => {
                    await this.savingData_fillItem(item, i, "active", preparedData);
                }),
            );
        }

        if (data.notStarted.length > 0) {
            await Promise.all(
                data.notStarted.map(async (item: any, i: number) => {
                    await this.savingData_fillItem(item, i, "notStarted", preparedData);
                }),
            );
        }

        if (data.completed.length > 0) {
            await Promise.all(
                data.completed.map(async (item: any, i: number) => {
                    await this.savingData_fillItem(item, i, "completed", preparedData);
                }),
            );
        }

        this.mappingOfStatusesService.Steps = preparedData.allSteps;

        // Build dashboardMapping array for analytics from the input data
        const dashboardMapping: any[] = [];
        const dataTyped = data as any;
        Object.values(ViewType).forEach((viewState) => {
            if (dataTyped[viewState] && Array.isArray(dataTyped[viewState])) {
                dataTyped[viewState].forEach((step: any) => {
                    if (step.Statuses && Array.isArray(step.Statuses)) {
                        step.Statuses.forEach((status: any) => {
                            const statusName = status.TaxReturnStatuse?.Name?.["en-CA"] || "";
                            let roleName = "";
                            // For non-J&B dashboards, include all roles (including unassigned)
                            if (!isJobsBillingDashboard && status.RoleId) {
                                if (status.RoleId === "unassigned") {
                                    roleName = "Unassigned";
                                } else {
                                    const role = this.iFirmRolesService.getRoleById(status.RoleId);
                                    roleName = role?.Description?.["en-CA"] || "";
                                }
                            }
                            dashboardMapping.push({
                                view: viewState,
                                stepName: step.Name["en-CA"],
                                status: statusName,
                                role: roleName,
                            });
                        });
                    }
                });
            }
        });

        this.analyticsService.track("Save Mapping of statuses", { dashboardMapping });

        if (preparedData.addSteps.length > 0) {
            preparedData.addSteps.forEach((newStep: any) => {
                this.isSaving = ++this.isSaving;
                this.stepsService.addStep(newStep).subscribe(
                    (response: any) => {
                        this.stepsService.refresh();
                        this.mappingViewmodel[response.Step.State][response.Step.Order].Id = response.Step.Id;
                        this.savingData_saveToMappingOfStatusesService(response.Step.State, response.Step.Order, response.Step.Id);
                        delete this.mappingViewmodel[response.Step.State][response.Step.Order].tempCollumnId;
                        this.isSaving = --this.isSaving;
                    },
                    () => {
                        this.isSaving = --this.isSaving;
                        this.inError = true;
                        this.savingData_returnObservableIfNeeded();
                    },
                    () => {
                        this.savingData_returnObservableIfNeeded();
                    },
                );
            });
        }
        if (preparedData.updateSteps) {
            this.isSaving = ++this.isSaving;
            this.stepsService.updateStep(preparedData.updateSteps).subscribe(
                () => {
                    this.stepsService.refresh(true);
                    this.isSaving = --this.isSaving;
                },
                () => {
                    this.isSaving = --this.isSaving;
                    this.inError = true;
                    this.savingData_returnObservableIfNeeded();
                },
                () => {
                    this.savingData_returnObservableIfNeeded();
                },
            );
        }
        return this.inError;
    }

    savingData_returnObservableIfNeeded() {
        if (this.isSaving === 0) {
            if (this.inError) {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "ERROR",
                    messageCode: "CancelSaved",
                });
            } else {
                this.responseErrorHandlerService.showPopup({
                    typeLayout: "SUCCESS",
                    messageCode: "successfullySaved",
                });
            }
            return this.inError;
        }
    }

    async savingData_fillItem(step, i, State, preparedData) {
        const Statuses = await Promise.all(
            step.Statuses.map(async (item) => {
                if (item.TaxReturnStatuse?.Id) {
                    await this.savingData_findChangedStatuses(item.TaxReturnStatuse);
                } else {
                    await this.savingData_findChangedStatuses(item);
                }
                return {
                    RoleId: item.RoleId === "unassigned" ? "" : item.RoleId,
                    TaxReturnStatusId: item.TaxReturnStatusId,
                };
            }),
        );
        const temp: any = {
            Name: step.Name,
            State: State,
            Order: i,
            Statuses: Statuses,
        };

        const tempForModel: any = {
            Id: step.Id,
            Name: step.Name,
            State: State,
            Order: i,
            Statuses: Statuses,
        };

        if (step.tempCollumnId) {
            preparedData.addSteps.push(temp);
        } else {
            preparedData.updateSteps[step.Id] = temp;
        }
        preparedData.allSteps.push(tempForModel);
    }

    async savingData_findChangedStatuses(status: any) {
        let changedStatus: any = {};
        await Promise.all(
            this.originalStatuses.map(async (originalStatus) => {
                if (originalStatus.TaxReturnStatusId === status.Id) {
                    if (
                        originalStatus.TaxReturnStatuse.Name["en-CA"] !== status.Name["en-CA"] ||
                        originalStatus.TaxReturnStatuse.Name["fr-CA"] !== status.Name["fr-CA"]
                    ) {
                        const retStatus: ITaxReturnStatus = await this.taxReturnStatusesService.getTaxReturnStatusById(status.Id);
                        if (retStatus) {
                            retStatus.Name = status.Name;
                        }
                        changedStatus = {
                            Id: status.Id,
                            ReturnStatusKey: status.ReturnStatusKey,
                            Name: {
                                "en-CA": status.Name["en-CA"],
                                "fr-CA": status.Name["fr-CA"],
                            },
                            TaxAppStatusId: status.TaxAppStatusId,
                        };
                    }
                }
            }),
        );
        if (changedStatus.Id) {
            this.taxReturnStatusesService.updateStatus(changedStatus.Id, changedStatus).subscribe((savedStatus: any) => {
                this.taxReturnStatusesService.reload();
                this.originalStatuses.forEach((originalStatus) => {
                    if (savedStatus?.TaxReturnStatus.Id === originalStatus.TaxReturnStatusId) {
                        originalStatus.TaxReturnStatuse.Name = {
                            "en-CA": savedStatus.TaxReturnStatus.Name["en-CA"],
                            "fr-CA": savedStatus.TaxReturnStatus.Name["fr-CA"],
                        };
                    }
                });
            });
        }
    }

    savingData_saveToMappingOfStatusesService(state, order, Id) {
        for (const step of this.mappingOfStatusesService.Steps) {
            if (step.Order === order && step.State === state) {
                step.Id = Id;
            }
        }
    }

    async onColumnDelete(column: any) {
        await firstValueFrom(this.stepsService.deleteStep(column.Id));
        const stepIndex = this.Steps.findIndex((step) => step.Id === column.Id);
        this.Steps.splice(stepIndex, 1);
        this.mappingOfStatusesService.Steps.splice(stepIndex, 1);
    }

    softDeleteColumn(status: string, column: any, i: number): void {
        this.mappingViewmodel.unassigned = this.mappingViewmodel.unassigned.concat(column.Statuses);
        this.mappingViewmodel[status].splice(i, 1);
    }
}
